export interface FeedingRecord {
  id: number;
  stack_id: number;
  feed_type_id: number;
  feeding_datetime: string;
  amount_kg: number;
  consumption_activity?: 'high' | 'medium' | 'low';
  notes?: string;
  created_at: string;
}

export interface CreateFeedingData {
  stack_id: number;
  feed_type_id: number;
  amount_kg: number;
  consumption_activity?: 'high' | 'medium' | 'low';
  notes?: string;
}

class FeedingService {
  private baseUrl = '/api/v1';

  async getFeedingRecords(stackId?: number): Promise<FeedingRecord[]> {
    try {
      const url = stackId 
        ? `${this.baseUrl}/feeding-records?stack_id=${stackId}`
        : `${this.baseUrl}/feeding-records`;
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Ошибка загрузки записей кормления');
      }
      return await response.json();
    } catch (error) {
      console.error('Ошибка загрузки записей кормления:', error);
      throw error;
    }
  }

  async createFeedingRecord(data: CreateFeedingData): Promise<FeedingRecord> {
    try {
      const response = await fetch(`${this.baseUrl}/feeding-records`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || 'Ошибка создания записи кормления');
      }

      return await response.json();
    } catch (error) {
      console.error('Ошибка создания записи кормления:', error);
      throw error;
    }
  }

  async getFeedingHistory(days: number = 30): Promise<FeedingRecord[]> {
    try {
      const response = await fetch(`${this.baseUrl}/feeding-records/history?days=${days}`);
      if (!response.ok) {
        throw new Error('Ошибка загрузки истории кормления');
      }
      return await response.json();
    } catch (error) {
      console.error('Ошибка загрузки истории кормления:', error);
      throw error;
    }
  }

  async updateFeedingRecord(id: number, data: Partial<CreateFeedingData>): Promise<FeedingRecord> {
    try {
      const response = await fetch(`${this.baseUrl}/feeding-records/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error('Ошибка обновления записи кормления');
      }

      return await response.json();
    } catch (error) {
      console.error('Ошибка обновления записи кормления:', error);
      throw error;
    }
  }

  async deleteFeedingRecord(id: number): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/feeding-records/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Ошибка удаления записи кормления');
      }
    } catch (error) {
      console.error('Ошибка удаления записи кормления:', error);
      throw error;
    }
  }

  // Быстрое кормление всех активных стеллажей
  async quickFeedAll(feedTypeId: number, amountKg: number): Promise<FeedingRecord[]> {
    try {
      const response = await fetch(`${this.baseUrl}/feeding-records/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          feed_type_id: feedTypeId,
          amount_kg: amountKg,
          consumption_activity: 'medium'
        }),
      });

      if (!response.ok) {
        throw new Error('Ошибка массового кормления');
      }

      return await response.json();
    } catch (error) {
      console.error('Ошибка массового кормления:', error);
      throw error;
    }
  }

  // Получить статистику кормления
  async getFeedingStats(stackId?: number): Promise<any> {
    try {
      const url = stackId 
        ? `${this.baseUrl}/feeding-records/stats?stack_id=${stackId}`
        : `${this.baseUrl}/feeding-records/stats`;
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Ошибка загрузки статистики кормления');
      }
      return await response.json();
    } catch (error) {
      console.error('Ошибка загрузки статистики кормления:', error);
      throw error;
    }
  }
}

export const feedingService = new FeedingService();

// Экспорт функций для удобного использования
export const createFeedingRecord = (data: CreateFeedingData) => feedingService.createFeedingRecord(data);
export const getFeedingRecords = (stackId?: number) => feedingService.getFeedingRecords(stackId);
export const getFeedingHistory = (days?: number) => feedingService.getFeedingHistory(days);
export const updateFeedingRecord = (id: number, data: Partial<CreateFeedingData>) => feedingService.updateFeedingRecord(id, data);
export const deleteFeedingRecord = (id: number) => feedingService.deleteFeedingRecord(id);
export const quickFeedAll = (feedTypeId: number, amountKg: number) => feedingService.quickFeedAll(feedTypeId, amountKg);
export const getFeedingStats = (stackId?: number) => feedingService.getFeedingStats(stackId);
